/**
 * Variant-specific product-grid hover image.
 *
 * Keeps each product card's hover image (`.hover-img.js-variant-hover-img`) in
 * sync with the SELECTED variant's `custom.grid_hover_image` metafield.
 *
 * Data (emitted by snippets/card-product.liquid, so this works in EVERY grid:
 * collection, featured, recommendations, recently viewed, search, etc.):
 *   - `.js-variant-hover-images`  <script type="application/json"> — a SLIM array,
 *       one entry per variant IN VARIANT ORDER: { "o": [optionValues], "u": "hoverUrl" }
 *       ("u" is "" when that variant has no grid_hover_image).
 *   - `.js-variant-hover-img`      the hover <img> (hidden when the selected variant has none)
 *
 * STRICT behaviour (TASK #11): the hover image is ONLY the selected variant's own
 * `custom.grid_hover_image`. There is NO fallback — if the selected variant has no
 * image, no hover image is shown (the base/featured image stays on hover, via the
 * `uh-hover-none` class toggled on `.product-card__media`).
 *
 * PERFORMANCE (TASK #9): this file NO LONGER parses the large
 * `[data-product-variants-json]` blob. That blob stays in the DOM for the theme's
 * baseline ProductCard / quick-cart logic, but the hover feature now reads only the
 * slim `.js-variant-hover-images` array above — far less JSON to parse per card.
 * Parsing is also lazy (IntersectionObserver + idle) and cached per card, so it
 * never runs as one big synchronous task on load. Behaviour is unchanged: the
 * default hover src is already correct from the server render, and hover images
 * still update on swatch change/click.
 */
(function () {
  if (window.__uhVariantHoverInit) return; // guard: run once even if included twice
  window.__uhVariantHoverInit = true;

  // Per-card parse cache for the slim hover array (parse at most once per card).
  var entriesCache = new WeakMap();

  var idle = window.requestIdleCallback || function (cb) {
    return setTimeout(function () { cb({ didTimeout: false, timeRemaining: function () { return 0; } }); }, 1);
  };

  function parseJSON(el) {
    if (!el || !el.textContent) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function getCard(node) {
    if (!node || !node.closest) return null;
    return node.closest('product-card') || node.closest('.product-card');
  }

  // Slim entries: [{ o: [optionValues], u: 'hoverUrl' }, ...] in variant order.
  function getEntries(card) {
    if (entriesCache.has(card)) return entriesCache.get(card);
    var arr = parseJSON(card.querySelector('.js-variant-hover-images'));
    if (!Array.isArray(arr)) arr = null;
    entriesCache.set(card, arr);
    return arr;
  }

  // Resolve the hover entry for the currently checked swatches, mirroring the old
  // logic: the FIRST variant (in order) whose option values include every checked
  // value; falls back to the first entry when nothing is selected / no match.
  function resolveEntry(card) {
    var entries = getEntries(card);
    if (!entries || !entries.length) return null;

    var checked = card.querySelectorAll('.variant-option-radio-input:checked');
    var values = Array.prototype.map.call(checked, function (i) { return i.value; });
    if (!values.length) return entries[0];

    var match = entries.find(function (e) {
      var opts = e.o || [];
      return values.every(function (val) { return opts.indexOf(val) !== -1; });
    });
    return match || entries[0];
  }

  function updateHoverImage(card) {
    if (!card) return;
    var hoverImg = card.querySelector('.js-variant-hover-img');
    if (!hoverImg) return; // this card has no variant hover images -> nothing to do

    var media = card.querySelector('.product-card__media');
    var entry = resolveEntry(card);
    // STRICT (TASK #11): only the selected variant's own image. No fallback chain.
    var url = (entry && entry.u) ? entry.u : '';

    if (url) {
      if (hoverImg.getAttribute('src') !== url) {
        hoverImg.removeAttribute('srcset'); // ensure our src wins
        hoverImg.setAttribute('src', url);
      }
      hoverImg.style.removeProperty('display');
      if (media) media.classList.remove('uh-hover-none');
    } else {
      // Selected variant has no grid_hover_image -> show NO hover image (no fallback).
      // Hide the hover <img> and let `uh-hover-none` keep the base image on hover.
      hoverImg.style.display = 'none';
      hoverImg.removeAttribute('src');
      if (media) media.classList.add('uh-hover-none');
    }
  }

  // Swatch change (keyboard, programmatic, most desktop interactions).
  document.addEventListener('change', function (e) {
    var input = e.target && e.target.closest && e.target.closest('.variant-option-radio-input');
    if (input) updateHoverImage(getCard(input));
  });

  // Swatch click/tap (labels + touch devices) — run after the theme updates state.
  document.addEventListener('click', function (e) {
    var swatch = e.target && e.target.closest && e.target.closest('.button--variant, .product-option__label');
    if (!swatch) return;
    var card = getCard(swatch);
    if (card) setTimeout(function () { updateHoverImage(card); }, 0);
  });

  // Lazy priming (safety re-sync of the initial state — the server already renders
  // the correct default src, so this is intentionally OFF the critical path):
  // process each card only when it nears the viewport, during idle time.
  function primeLazily(root) {
    var cards = (root || document).querySelectorAll('product-card, .product-card');
    if (!cards.length) return;

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var card = entry.target;
          obs.unobserve(card);
          idle(function () { updateHoverImage(card); });
        });
      }, { rootMargin: '200px 0px' });
      cards.forEach(function (c) { io.observe(c); });
    } else {
      // No IntersectionObserver: process in small idle chunks so we never block.
      var i = 0;
      (function chunk() {
        var end = Math.min(i + 4, cards.length);
        for (; i < end; i++) { updateHoverImage(cards[i]); }
        if (i < cards.length) idle(chunk);
      })();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { primeLazily(); });
  } else {
    primeLazily();
  }
  document.addEventListener('shopify:section:load', function (e) { primeLazily(e.target); });
})();
